import { useCallback, useEffect, ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { AccountPermission } from 'modules/permissions/constants';
import { guardDialogSlice } from 'modules/guardDialog';
import { homeRoutesConfig } from 'domains/home/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';

export interface GuardAccountPermissionProps {
  children: ReactNode;
  fallback?: ReactNode;
  permission: AccountPermission;
  shouldForceRedirect?: boolean;
  shouldHideAlert?: boolean;
  shouldRedirect?: boolean;
}

export const GuardAccountPermission = ({
  children,
  fallback = null,
  permission,
  shouldForceRedirect,
  shouldHideAlert,
  shouldRedirect,
}: GuardAccountPermissionProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const hasPremission = useCheckAccountPermission({ permission });

  const { isLoggedIn } = useAuth();
  const { isAccountPremissionDialogHidden, permissionsInitialized } =
    useAccount();
  const hasAccess = hasPremission && isLoggedIn;

  const showAlert = useCallback(() => {
    dispatch(guardDialogSlice.actions.showDialog());
  }, [dispatch]);

  useEffect(() => {
    if (
      (isLoggedIn && !hasAccess && permissionsInitialized && shouldRedirect) ||
      shouldForceRedirect
    ) {
      const homePage = homeRoutesConfig.home.generatePath();

      navigate(homePage, { replace: true });

      const isAlertHidden = isAccountPremissionDialogHidden || shouldHideAlert;

      // show notification only if redirect hasn't been forced
      if (
        permissionsInitialized &&
        !hasAccess &&
        shouldRedirect &&
        !isAlertHidden
      ) {
        showAlert();
      }
    }
  }, [
    hasAccess,
    navigate,
    isAccountPremissionDialogHidden,
    isLoggedIn,
    permission,
    permissionsInitialized,
    shouldForceRedirect,
    shouldHideAlert,
    shouldRedirect,
    showAlert,
  ]);

  if (hasAccess || !isLoggedIn) {
    return <>{children}</>;
  }

  return fallback;
};
